/**
 * Affiliation visibility rules, read from the BU_AFFILIATION_GA_VISIBILITY IO
 * Configuration (TEXT holding JSON).
 *
 * Replaces two separate Yes/No configs that answered the same question on either side of
 * the same relationship.
 *
 * Expected shape (lowercase, because the io-config framework lowercases TEXT values):
 * {
 *   "contactform": { "sync": "primary", "affiliatedaccountsonline": "all" },
 *   "accountform": { "sync": "none",
 *                    "affiliatedcontactsonline": "all",
 *                    "affiliatedaccountsonline": "all" }
 * }
 *
 * Each online key names the list it governs, because the account form has two of them:
 * affiliated contacts (account-contact) and affiliated accounts (account-account). The
 * contact form has one, its affiliated accounts.
 *
 * The earlier single key "online" is still read as a fallback, so a BU configured before
 * the account-account list existed keeps working without an edit: on the contact form it
 * means affiliated accounts, on the account form affiliated contacts - exactly what it
 * meant then. It never implies the account-account list, which stays 'none' until set.
 */

/**
 * What the periodic sync stores beyond the rep's position scope.
 * - none:    position-scoped only
 * - primary: also the primary affiliation, stored and therefore available offline
 *
 * There is deliberately no 'all' here. Syncing every affiliation is the behaviour that
 * caused the full-sync over-fetch, and offering it as a config value would invite turning
 * it back on. Everything-visibility belongs on the online axis, where nothing is stored.
 */
export type AffiliationSyncScope = 'none' | 'primary';

/**
 * What a live fetch adds when the form is opened online.
 * - none: nothing; the form shows whatever sync stored
 * - all:  every affiliation regardless of position, never stored
 */
export type AffiliationOnlineScope = 'none' | 'all';

/** Contact form: one affiliated list, the accounts this contact is affiliated to. */
export interface ContactFormAffiliationVisibility {
  sync: AffiliationSyncScope;
  affiliatedAccountsOnline: AffiliationOnlineScope;
}

/** Account form: two affiliated lists, independently switchable. */
export interface AccountFormAffiliationVisibility {
  sync: AffiliationSyncScope;
  affiliatedContactsOnline: AffiliationOnlineScope;
  affiliatedAccountsOnline: AffiliationOnlineScope;
}

export interface AffiliationVisibilityConfig {
  contactForm: ContactFormAffiliationVisibility;
  accountForm: AccountFormAffiliationVisibility;
}

const CONTACT_NONE: ContactFormAffiliationVisibility = { sync: 'none', affiliatedAccountsOnline: 'none' };
const ACCOUNT_NONE: AccountFormAffiliationVisibility = {
  sync: 'none',
  affiliatedContactsOnline: 'none',
  affiliatedAccountsOnline: 'none',
};

export const DEFAULT_AFFILIATION_VISIBILITY: AffiliationVisibilityConfig = {
  contactForm: { ...CONTACT_NONE },
  accountForm: { ...ACCOUNT_NONE },
};

/** Keys arrive lowercased by the io-config framework, but an admin may type camelCase. */
function ciProp(obj: any, key: string): any {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }
  if (key in obj) {
    return obj[key];
  }
  const lower = key.toLowerCase();
  const match = Object.keys(obj).find(k => k.toLowerCase() === lower);
  return match ? obj[match] : undefined;
}

function readEnum<T extends string>(source: any, key: string, allowed: T[]): T | null {
  const raw = ciProp(source, key);
  if (typeof raw !== 'string') {
    return null;
  }
  const value = raw.trim().toLowerCase() as T;
  return allowed.includes(value) ? value : null;
}

const ONLINE_SCOPES: AffiliationOnlineScope[] = ['none', 'all'];

function readSync(form: any): AffiliationSyncScope | null {
  return readEnum<AffiliationSyncScope>(form, 'sync', ['none', 'primary']);
}

function readContactForm(parsed: any): ContactFormAffiliationVisibility | null {
  const form = ciProp(parsed, 'contactform');
  if (!form) {
    return null;
  }
  const sync = readSync(form);
  // 'online' meant the affiliated-accounts list on this form before the keys were named.
  const affiliatedAccountsOnline = readEnum(form, 'affiliatedAccountsOnline', ONLINE_SCOPES)
    ?? readEnum(form, 'online', ONLINE_SCOPES);
  if (sync === null && affiliatedAccountsOnline === null) {
    return null;
  }
  return {
    sync: sync ?? 'none',
    affiliatedAccountsOnline: affiliatedAccountsOnline ?? 'none',
  };
}

function readAccountForm(parsed: any): AccountFormAffiliationVisibility | null {
  const form = ciProp(parsed, 'accountform');
  if (!form) {
    return null;
  }
  const sync = readSync(form);
  // 'online' meant the affiliated-contacts list on this form before the keys were named.
  const affiliatedContactsOnline = readEnum(form, 'affiliatedContactsOnline', ONLINE_SCOPES)
    ?? readEnum(form, 'online', ONLINE_SCOPES);
  const affiliatedAccountsOnline = readEnum(form, 'affiliatedAccountsOnline', ONLINE_SCOPES);
  if (sync === null && affiliatedContactsOnline === null && affiliatedAccountsOnline === null) {
    return null;
  }
  return {
    sync: sync ?? 'none',
    affiliatedContactsOnline: affiliatedContactsOnline ?? 'none',
    affiliatedAccountsOnline: affiliatedAccountsOnline ?? 'none',
  };
}

/** Returns null when the value is missing or unparseable, so the caller can fall back. */
export function parseAffiliationVisibilityConfig(
  configValue: string | null | undefined,
): AffiliationVisibilityConfig | null {
  if (!configValue) {
    return null;
  }
  try {
    const parsed = JSON.parse(configValue);
    const contactForm = readContactForm(parsed);
    const accountForm = readAccountForm(parsed);
    if (!contactForm && !accountForm) {
      return null;
    }
    return {
      contactForm: contactForm ?? { ...CONTACT_NONE },
      accountForm: accountForm ?? { ...ACCOUNT_NONE },
    };
  } catch {
    return null;
  }
}

/** A BU with no record, or an unreadable one, gets position-scoped behaviour only. */
export function resolveAffiliationVisibility(
  configValue: string | null | undefined,
): AffiliationVisibilityConfig {
  return parseAffiliationVisibilityConfig(configValue) ?? {
    contactForm: { ...CONTACT_NONE },
    accountForm: { ...ACCOUNT_NONE },
  };
}
